import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  TextField,
  Typography
} from '@mui/material';

import { AddOutlined } from '@mui/icons-material';

import ICD10DiagnosisDialog from '../Diagnosis/ICD10DiagnosisDialog';

import { OrdersDialogStyles } from './OrdersDialogStyles';
import { multitextFieldSx } from '../Styles/InputStyles';

const OrderSection = ({
  title,
  placeholder,
  value,
  onChange,
  onAdd
}) => {
  return (
    <Box sx={OrdersDialogStyles.panel}>
      <Box sx={OrdersDialogStyles.panelHeader}>
        <Typography sx={OrdersDialogStyles.panelTitle}>
          {title}
        </Typography>

        <IconButton
          size="small"
          aria-label={`Add ${title}`}
          onClick={onAdd}
          sx={OrdersDialogStyles.addButton}
        >
          <AddOutlined sx={OrdersDialogStyles.addIcon} />
        </IconButton>
      </Box>

      <Box sx={OrdersDialogStyles.fieldWrapper}>
        <TextField
          fullWidth
          multiline
          minRows={5}
          maxRows={5}
          value={value}
          placeholder={placeholder}
          onChange={event => onChange(event.target.value)}
          sx={multitextFieldSx}
        />
      </Box>
    </Box>
  );
};

OrderSection.propTypes = {
  title: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onAdd: PropTypes.func
};

OrderSection.defaultProps = {
  value: '',
  onChange: () => {},
  onAdd: () => {}
};

const DiagnosisSection = ({
  diagnosis,
  onDiagnosisChange,
  onAdd
}) => {
  return (
    <Box sx={OrdersDialogStyles.panel}>
      <Box sx={OrdersDialogStyles.panelHeader}>
        <Typography sx={OrdersDialogStyles.panelTitle}>
          Diagnosis
        </Typography>

        <IconButton
          size="small"
          aria-label="Add diagnosis"
          onClick={onAdd}
          sx={OrdersDialogStyles.addButton}
        >
          <AddOutlined sx={OrdersDialogStyles.addIcon} />
        </IconButton>
      </Box>

      <Box sx={OrdersDialogStyles.fieldWrapper}>
        <TextField
          fullWidth
          multiline
          minRows={5}
          maxRows={5}
          value={diagnosis}
          placeholder="Select a diagnosis template, or input diagnosis details..."
          onChange={event =>
            onDiagnosisChange(event.target.value)
          }
          sx={multitextFieldSx}
        />
      </Box>
    </Box>
  );
};

DiagnosisSection.propTypes = {
  diagnosis: PropTypes.string,
  onDiagnosisChange: PropTypes.func,
  onAdd: PropTypes.func
};

DiagnosisSection.defaultProps = {
  diagnosis: '',
  onDiagnosisChange: () => {},
  onAdd: () => {}
};

const OrderEntry = ({
  orderTitle,
  orderPlaceholder,
  orderValue,
  diagnosis,
  selectedICD10Diagnoses,
  isDirty,
  onOrderChange,
  onDiagnosisChange,
  onSelectedICD10DiagnosesChange,
  onDirtyChange,
  onOpenOrderTemplates
}) => {
  const [openICD10Diagnosis, setOpenICD10Diagnosis] =
    useState(false);

  const handleOpenDiagnosisDialog = () => {
    setOpenICD10Diagnosis(true);
  };

  const handleSaveDiagnoses = diagnoses => {
    onSelectedICD10DiagnosesChange(diagnoses);
    onDirtyChange(true);

    const diagnosisText = diagnoses
      .map(item => {
        const code = item.code || item.icdCode || '';
        const description =
          item.description || item.name || '';

        return [code, description]
          .filter(Boolean)
          .join(' - ');
      })
      .join('\n');

    onDiagnosisChange(diagnosisText);
    setOpenICD10Diagnosis(false);
  };

  return (
    <>
      <Box sx={OrdersDialogStyles.leftColumn}>
        <OrderSection
          title={orderTitle}
          placeholder={orderPlaceholder}
          value={orderValue}
          onChange={onOrderChange}
          onAdd={onOpenOrderTemplates}
        />

        <DiagnosisSection
          diagnosis={diagnosis}
          onDiagnosisChange={onDiagnosisChange}
          onAdd={handleOpenDiagnosisDialog}
        />
      </Box>

      <ICD10DiagnosisDialog
        open={openICD10Diagnosis}
        selectedDiagnoses={selectedICD10Diagnoses}
        onClose={() => setOpenICD10Diagnosis(false)}
        onSave={handleSaveDiagnoses}
      />
    </>
  );
};

OrderEntry.propTypes = {
  orderTitle: PropTypes.string,
  orderPlaceholder: PropTypes.string,
  orderValue: PropTypes.string,
  diagnosis: PropTypes.string,
  selectedICD10Diagnoses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      code: PropTypes.string,
      icdCode: PropTypes.string,
      description: PropTypes.string,
      name: PropTypes.string
    })
  ),
  isDirty: PropTypes.bool,
  onOrderChange: PropTypes.func,
  onDiagnosisChange: PropTypes.func,
  onSelectedICD10DiagnosesChange: PropTypes.func,
  onDirtyChange: PropTypes.func,
  onOpenOrderTemplates: PropTypes.func
};

OrderEntry.defaultProps = {
  orderTitle: 'Lab Orders',
  orderPlaceholder:
    'Select an order template, or input order details...',
  orderValue: '',
  diagnosis: '',
  selectedICD10Diagnoses: [],
  isDirty: false,
  onOrderChange: () => {},
  onDiagnosisChange: () => {},
  onSelectedICD10DiagnosesChange: () => {},
  onDirtyChange: () => {},
  onOpenOrderTemplates: () => {}
};

export default OrderEntry;